import { useQuery } from '@tanstack/react-query';
import ProductCards from '../components/ProductCards';
import { getAccessories } from '../service/getAccessories';
import { useContext, useEffect, useState } from 'react';
import {
  AppliedFilterValues,
  NonScriptRangeProps,
  Product,
  ReactSelectOptionProp,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import showToast from './ToastComponent';
import { CartContext } from '../context/cartContext';
import { useGetProductFormulations } from '../query/getProductFormulationQuery';
import { useProductBrands } from '../query/getProductBrandQuery';
import { useGetProductConditionsQuery } from '../query/getProductConditionsQuery';
import NonScriptFilters from './NonScriptFilters';
import LoadingWrapper from './LoadingWrapper';
import { CloseIcon } from '../assets/svgImages';

export function NonScriptRangeProducts(props: NonScriptRangeProps) {
  const [selectedBrand, setSelectedBrand] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [selectedForm, setSelectedForm] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [selectedArea, setSelectedArea] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const { user } = useAuth();
  const { cart } = useContext(CartContext);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilterValues>({
    brand: [],
    form: [],
    area: [],
  });

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    try {
      const storedBrand = localStorage.getItem('selectedBrand');
      const storedForm = localStorage.getItem('selectedForm');
      const storedArea = localStorage.getItem('selectedArea');

      const brand = storedBrand ? JSON.parse(storedBrand) : [];
      const form = storedForm ? JSON.parse(storedForm) : [];
      const area = storedArea ? JSON.parse(storedArea) : [];

      setSelectedBrand(brand);
      setSelectedForm(form);
      setSelectedArea(area);

      setAppliedFilters({
        brand,
        form,
        area,
      });
    } catch (error) {
      clearAllFilters();
    }
  }, []);

  useEffect(() => {
    const count =
      (selectedBrand?.length > 0 ? 1 : 0) +
      (selectedForm?.length > 0 ? 1 : 0) +
      (selectedArea?.length > 0 ? 1 : 0);

    setFilterCount(count);
  }, [selectedBrand, selectedForm, selectedArea]);

  const { data, error, isLoading } = useQuery({
    queryKey: ['accessories', user?.access_token, appliedFilters, cart],
    queryFn: ({ queryKey }) => {
      const [, token, appliedFilters] = queryKey;
      const filters = appliedFilters as AppliedFilterValues;
      return getAccessories(
        token as string,
        filters.brand,
        filters.form,
        filters.area,
        cart,
      );
    },
    enabled: !!user?.access_token,
  });

  const { brands, isBrandLoading } = useProductBrands(user?.access_token);

  const { formulation, isFormLoading } = useGetProductFormulations(
    user?.access_token,
  );
  const { condition, isError, isConditionLoading } =
    useGetProductConditionsQuery(user?.access_token);

  useEffect(() => {
    if (error || isError) {
      showToast({
        message: 'Unable to fetch accessories!',
        type: 'error',
      });
    }
  }, [error, isError]);

  const clearAllFilters = () => {
    setSelectedBrand([]);
    setSelectedForm([]);
    setSelectedArea([]);

    setAppliedFilters({
      brand: [],
      form: [],
      area: [],
    });
    localStorage.removeItem('selectedBrand');
    localStorage.removeItem('selectedForm');
    localStorage.removeItem('selectedArea');
  };

  const applyFilters = () => {
    setAppliedFilters({
      brand: selectedBrand,
      form: selectedForm,
      area: selectedArea,
    });

    setMobileFiltersOpen(false);
  };

  const loading =
    isLoading || isFormLoading || isBrandLoading || isConditionLoading;

  return (
    <LoadingWrapper state={loading}>
      <div className="result-block-container">
        <div className="flex justify-end items-center mb-4 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(true)}
            className="inline-flex items-center rounded-md bg-primary-blue-500 px-3 py-2 text-sm font-medium text-white shadow-sm"
          >
            ☰ Filters
          </button>

          {filterCount > 0 ? (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ms-3 inline-flex items-center rounded-md bg-primary-blue-500 px-3 py-2 text-sm font-medium text-white shadow-sm"
            >
              {`(${filterCount}) clear`}
            </button>
          ) : (
            <></>
          )}
        </div>

        {mobileFiltersOpen && (
          <div className="fixed inset-0 z-50 lg:hidden overflow-hidden">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <div className="fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col bg-white rounded-t-2xl shadow-2xl p-6 max-h-[80vh]">
              <div className="overflow-y-auto overscroll-contain touch-pan-y">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-primary-blue-500">
                    Filters
                  </h2>

                  <CloseIcon
                    onClick={() => setMobileFiltersOpen(false)}
                    className="cursor-pointer fill-primary-blue-500 sm:w-4 sm:h-4"
                  />
                </div>

                <NonScriptFilters
                  brands={brands}
                  selectedBrand={selectedBrand}
                  setSelectedBrand={setSelectedBrand}
                  isBrandLoading={isBrandLoading}
                  formulation={formulation}
                  selectedForm={selectedForm}
                  setSelectedForm={setSelectedForm}
                  isFormLoading={isFormLoading}
                  conditions={condition}
                  selectedArea={selectedArea}
                  setSelectedArea={setSelectedArea}
                  isConditionLoading={isConditionLoading}
                />

                <div className="flex justify-end items-center">
                  <button
                    onClick={applyFilters}
                    className="w-auto text-white p-2 rounded-md bg-primary-blue-500"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="hidden lg:block">
          <NonScriptFilters
            brands={brands}
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            isBrandLoading={isBrandLoading}
            formulation={formulation}
            selectedForm={selectedForm}
            setSelectedForm={setSelectedForm}
            isFormLoading={isFormLoading}
            conditions={condition}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            isConditionLoading={isConditionLoading}
            setAppliedFilters={setAppliedFilters}
          />
        </div>

        <ul className="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
          {data && data?.length > 0 ? (
            data?.map((product: Product) => (
              <li key={product.id}>
                <ProductCards
                  addProductToCart={async (id: string) => {
                    await props.addToCartMutation.mutateAsync(id);
                  }}
                  removeProductFromCart={async (productId: string) => {
                    await props.removeFromCartMutation.mutateAsync(productId);
                  }}
                  product={product}
                  substitueCard={false}
                  isAccessories
                  allowedProduct={false}
                />
              </li>
            ))
          ) : (
            <p>No Non-Script Range Products Exist</p>
          )}
        </ul>
      </div>
    </LoadingWrapper>
  );
}
